const SPEECH_RATE = 0.9
const NATIVE_START_TIMEOUT = 3000

export class PainelVoice {
  constructor({
    speechSynthesis,
    SpeechSynthesisUtterance,
    fallback = null,
    timerHost = global,
    setTimer = timerHost.setTimeout,
    clearTimer = timerHost.clearTimeout,
    nativeStartTimeout = NATIVE_START_TIMEOUT
  }) {
    this.speechSynthesis = speechSynthesis
    this.SpeechSynthesisUtterance = SpeechSynthesisUtterance
    this.fallback = fallback
    this.setTimer = setTimer.bind(timerHost)
    this.clearTimer = clearTimer.bind(timerHost)
    this.nativeStartTimeout = nativeStartTimeout
    this.activeOperation = null
    if (this.fallback) this.fallback.disablePermissionPopup = true
  }

  get nativeAvailable() {
    return Boolean(this.speechSynthesis && this.SpeechSynthesisUtterance)
  }

  initializePermissions() {
    if (this.nativeAvailable) {
      try {
        const utterance = new this.SpeechSynthesisUtterance(' ')
        utterance.lang = 'pt-BR'
        this.speechSynthesis.speak(utterance)
      } catch (error) {
        // The real announcement will use ResponsiveVoice when native speech is broken.
      }
    }
    this.fallback?.initializePermissions?.()
  }

  speak(text, voiceName, options = {}) {
    const operation = {
      settled: false,
      fallbackStarted: false,
      timer: null
    }
    this.activeOperation = operation

    const clearStartTimer = () => {
      this.clearTimer(operation.timer)
      operation.timer = null
    }
    const finish = (callback, value) => {
      if (operation.settled) return
      operation.settled = true
      clearStartTimer()
      if (this.activeOperation === operation) this.activeOperation = null
      callback?.(value)
    }
    const startFallback = (cause, cancelNative = false) => {
      if (operation.settled || operation.fallbackStarted) return
      operation.fallbackStarted = true
      clearStartTimer()
      if (cancelNative) {
        try {
          this.speechSynthesis?.cancel?.()
        } catch (error) {
          // Native cancellation is best effort; fallback must still start.
        }
      }

      if (!this.fallback?.speak) {
        finish(options.onerror, cause || new Error('Voice unavailable'))
        return
      }

      try {
        this.fallback.forceFallbackMode?.(true)
        this.fallback.speak(text, voiceName, {
          ...options,
          rate: SPEECH_RATE,
          onend: () => finish(options.onend),
          onerror: (error) => finish(options.onerror, error)
        })
      } catch (error) {
        finish(options.onerror, error)
      }
    }

    try {
      const voices = this.nativeAvailable
        ? this.speechSynthesis.getVoices?.() || []
        : []
      const nativeVoice = voices.find(({ lang }) => lang?.toLowerCase() === 'pt-br') ||
        voices.find(({ lang }) => lang?.toLowerCase().startsWith('pt'))

      if (!nativeVoice) {
        startFallback(new Error('Portuguese native voice unavailable'))
        return
      }

      const utterance = new this.SpeechSynthesisUtterance(text)
      utterance.lang = 'pt-BR'
      utterance.voice = nativeVoice
      utterance.rate = SPEECH_RATE
      utterance.onstart = clearStartTimer
      utterance.onend = () => {
        if (!operation.fallbackStarted) finish(options.onend)
      }
      utterance.onerror = (error) => startFallback(error, true)
      operation.timer = this.setTimer(
        () => startFallback(new Error('Native voice start timeout'), true),
        this.nativeStartTimeout
      )
      this.speechSynthesis.speak(utterance)
    } catch (error) {
      startFallback(error, true)
    }
  }

  cancel() {
    const operation = this.activeOperation
    if (operation) {
      operation.settled = true
      this.clearTimer(operation.timer)
      this.activeOperation = null
    }
    if (this.nativeAvailable) {
      try {
        this.speechSynthesis.cancel()
      } catch (error) {
        // Native cancellation is best effort; fallback must still be cancelled.
      }
    }
    this.fallback?.cancel?.()
  }
}
